"use client"

import * as Sentry from "@sentry/nextjs"
import { useEffect, useRef } from "react"
import { attemptChunkReload } from "~/lib/chunk-load-error"
import { currentLocationHrefWithoutQuery } from "~/lib/sentry-url"

/**
 * Top-level error boundary. Per Next.js convention this file REPLACES the root
 * layout when it fires, so it renders its own `<html>` / `<body>` with inline
 * styles (the Tailwind bundle may not be hydrated here). It catches chunk
 * errors that escape the route-level boundaries and self-heals stale bundles
 * after a redeploy via a single sessionStorage-guarded reload.
 */
export default function GlobalError({
  error,
  reset,
}: Readonly<{
  error: Error & { digest?: string }
  reset: () => void
}>) {
  const reloadedRef = useRef(false)

  useEffect(() => {
    if (globalThis.window === undefined) return
    Sentry.captureException(error, {
      tags: { boundary: "global-error" },
      extra: { digest: error.digest, href: currentLocationHrefWithoutQuery() },
    })
  }, [error])

  useEffect(() => {
    if (reloadedRef.current) return
    if (attemptChunkReload(error)) {
      reloadedRef.current = true
    }
  }, [error])

  return (
    <html lang="en">
      <body
        style={{
          fontFamily: "system-ui, sans-serif",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: 0,
          padding: "16px",
          background: "#fafafa",
          color: "#111",
        }}
      >
        <div style={{ textAlign: "center", maxWidth: "420px" }}>
          <h1 style={{ fontSize: "24px", fontWeight: 600, margin: "0 0 8px" }}>
            Something went wrong
          </h1>
          <p style={{ color: "#555", margin: "0 0 32px", lineHeight: 1.5 }}>
            An unexpected error occurred. Try refreshing the page.
          </p>
          <button
            type="button"
            onClick={reset}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              background: "#111",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 500,
            }}
          >
            Try Again
          </button>
          <p style={{ fontSize: "12px", color: "#999", marginTop: "32px" }}>Error 500</p>
        </div>
      </body>
    </html>
  )
}
